(() => {
  const DRAWER_SELECTOR = '[data-kukai-cart]';
  const SECTION_ID = 'kukai-cart-drawer';
  let addToCartBusy = false;

  function getDrawer() {
    return document.querySelector(DRAWER_SELECTOR);
  }

  function openCart() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('kukai-cart-open');
  }

  function closeCart() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('kukai-cart-open');
  }

  // Instantly reflects a new quantity in the UI for a given cart line,
  // without waiting for the server round-trip. The real refresh that
  // follows (subtotal, shipping bar, etc.) will reconcile shortly after;
  // this just removes the visible lag on the number itself.
  function updateQuantityDisplay(key, quantity) {
    document.querySelectorAll(`[data-cart-key="${key}"]`).forEach((item) => {
      const valueEl = item.querySelector('.kukai-cart-item__quantity-value');
      if (valueEl) valueEl.textContent = quantity;

      const minus = item.querySelector('[data-kukai-quantity-minus]');
      const plus = item.querySelector('[data-kukai-quantity-plus]');
      if (minus) minus.dataset.quantity = quantity;
      if (plus) plus.dataset.quantity = quantity;
    });
  }

  // Instantly removes a line item's whole card from the DOM, rather than
  // waiting for the full server round-trip. The background refresh that
  // follows still reconciles subtotal/shipping bar/etc against the real
  // cart state shortly after.
  function removeItemDisplay(key) {
    document.querySelectorAll(`[data-cart-key="${key}"]`).forEach((item) => {
      item.remove();
    });
  }

  async function refreshCartDrawer(openAfterRefresh = false) {
    try {
      const response = await fetch(
        `${window.location.pathname}?sections=${SECTION_ID}`,
        {
          method: 'GET',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        }
      );

      if (!response.ok) throw new Error('Unable to refresh cart');

      const sections = await response.json();
      const html = sections[SECTION_ID];

      if (!html) throw new Error('Cart section HTML not returned');

      const parser = new DOMParser();
      const documentHTML = parser.parseFromString(html, 'text/html');
      const newDrawer = documentHTML.querySelector(DRAWER_SELECTOR);
      const currentDrawer = getDrawer();

      if (newDrawer && currentDrawer) {
        // Preserve whatever open/closed state the drawer is currently in (or
        // force it open, if requested) on the INCOMING element before it's
        // inserted into the page. Swapping in an element that already has
        // the correct class means there's no closed -> open transition to
        // animate, which is what was causing the visible flicker on every
        // quantity change.
        const shouldBeOpen = openAfterRefresh || currentDrawer.classList.contains('is-open');

        if (shouldBeOpen) {
          newDrawer.classList.add('is-open');
          newDrawer.setAttribute('aria-hidden', 'false');
        }

        currentDrawer.replaceWith(newDrawer);

        if (shouldBeOpen) {
          document.body.classList.add('kukai-cart-open');
        }
      }

      await updateHeaderCartCount();
    } catch (error) {
      console.error('Kukai cart refresh error:', error);
    }
  }

  async function updateHeaderCartCount() {
    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return;

      const cart = await response.json();
      const count = cart.item_count;

      document.querySelectorAll('[data-kukai-cart-count]').forEach((element) => {
        element.textContent = count;
      });

      document.querySelectorAll('a[href="/cart"], a[href$="/cart"]').forEach((link) => {
        const text = link.textContent.trim();

        if (/^cart\s*\(/i.test(text)) {
          link.textContent = `cart ( ${count} )`;
        }
      });
    } catch (error) {
      console.error('Kukai cart count error:', error);
    }
  }

  // Tracks the most recent in-flight /cart/change.js request per line item,
  // so we can tell whether a given response is still the latest one that
  // matters (relevant if the user clicks +/- multiple times quickly).
  let latestChangeRequest = 0;

  async function changeQuantity(key, quantity) {
    // Update the UI immediately, before the network request even starts,
    // so there's no perceived lag on click. This is NOT gated on any
    // "busy" flag — each click should register right away, even if a
    // previous change is still being saved in the background.
    if (quantity <= 0) {
      removeItemDisplay(key);
    } else {
      updateQuantityDisplay(key, quantity);
    }

    const requestId = ++latestChangeRequest;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (!response.ok) throw new Error('Unable to update cart');

      const cartData = await response.json();

      if (requestId !== latestChangeRequest) return;

      if (cartData.item_count === 0) {
        // Nothing left in the cart — close the drawer instead of showing
        // the empty state. Still refresh the drawer's HTML and the header
        // count in the background so everything stays in sync for next
        // time it's opened, just without forcing it open now.
        closeCart();
        refreshCartDrawer(false);
      } else {
        // Only refresh the drawer's full HTML (subtotal, shipping bar, line
        // items) once, after the LAST quantity change has settled — avoids
        // an earlier, slower response clobbering a newer optimistic update.
        refreshCartDrawer(true);
      }
    } catch (error) {
      console.error('Kukai quantity error:', error);
      // The optimistic update above may now be wrong; re-sync from the
      // server regardless of ordering, since something went wrong.
      refreshCartDrawer(true);
    }
  }

  // Saves an edited gift-card message back to the cart line. /cart/change.js
  // REPLACES the whole properties object for a line, so we read the line's
  // full existing properties (stashed in a JSON blob in the markup) and only
  // overwrite the "Message" key, rather than sending just the message alone
  // and wiping out Recipient email/name/etc.
  //
  // Targeting by "line" (1-based position in cart.items) rather than "id"
  // (the variant:properties hash) — Shopify's own docs example uses "line"
  // for property updates, and the hashed key can become stale/mismatched
  // the moment properties change, which was causing the edit to appear to
  // save and then silently revert on the next refresh.
  async function changeMessage(line, properties, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
          properties: properties
        })
      });

      if (!response.ok) throw new Error('Unable to update message');

      await response.json();
      await refreshCartDrawer(true);
    } catch (error) {
      console.error('Kukai message update error:', error);
      refreshCartDrawer(true);
    }
  }

  async function addToCart(form) {
    if (addToCartBusy) return;
    addToCartBusy = true;

    try {
      const formData = new FormData(form);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(
          error.description ||
          error.message ||
          'Unable to add product to cart'
        );
      }

      await response.json();
      await refreshCartDrawer(true);
    } catch (error) {
      console.error('Kukai add to cart error:', error);
      alert(error.message);
    } finally {
      addToCartBusy = false;
    }
  }

  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-kukai-cart-close]');

    if (closeButton) {
      event.preventDefault();
      closeCart();
      return;
    }

    const cartOpen = event.target.closest(
      '[data-kukai-cart-open], a[href="/cart"], a[href$="/cart"]'
    );

    if (cartOpen) {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault();
      openCart();
      return;
    }

    const removeButton = event.target.closest('[data-kukai-cart-remove]');

    if (removeButton) {
      event.preventDefault();
      const key = removeButton.dataset.kukaiCartRemove;
      changeQuantity(key, 0);
      return;
    }

    const minusButton = event.target.closest('[data-kukai-quantity-minus]');

    if (minusButton) {
      event.preventDefault();
      const key = minusButton.dataset.key;
      const currentQuantity = Number(minusButton.dataset.quantity);
      changeQuantity(key, Math.max(0, currentQuantity - 1));
      return;
    }

    const plusButton = event.target.closest('[data-kukai-quantity-plus]');

    if (plusButton) {
      event.preventDefault();
      const key = plusButton.dataset.key;
      const currentQuantity = Number(plusButton.dataset.quantity);
      changeQuantity(key, currentQuantity + 1);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;

    const action = form.getAttribute('action') || '';

    if (
      action.includes('/cart/add') &&
      !form.hasAttribute('data-kukai-native-cart')
    ) {
      event.preventDefault();
      addToCart(form);
    }
  });

  // Save the edited message when the textarea loses focus. blur doesn't
  // bubble, so this listener is registered with useCapture: true.
  document.addEventListener(
    'blur',
    (event) => {
      const textarea = event.target.closest
        ? event.target.closest('[data-kukai-cart-message]')
        : null;

      if (!textarea) return;

      const item = textarea.closest('[data-cart-key]');
      if (!item) return;

      const line = Number(textarea.dataset.line);
      const propsScript = item.querySelector('[data-kukai-cart-properties]');
      let properties = {};

      if (propsScript) {
        try {
          properties = JSON.parse(propsScript.textContent);
        } catch (error) {
          properties = {};
        }
      }

      const newValue = textarea.value;

      if (properties['Message'] === newValue) return;

      properties['Message'] = newValue;

      const quantityValueEl = item.querySelector('.kukai-cart-item__quantity-value');
      const quantity = quantityValueEl
        ? Number(quantityValueEl.textContent.trim()) || 1
        : 1;

      changeMessage(line, properties, quantity);
    },
    true
  );

  // Live-updates the "x/200" character counter under the message textarea
  // as the person types. input bubbles, so no capture needed here.
  document.addEventListener('input', (event) => {
    const textarea = event.target.closest
      ? event.target.closest('[data-kukai-cart-message]')
      : null;

    if (!textarea) return;

    const field = textarea.closest('.kukai-cart-item__message-field');
    const counter = field
      ? field.querySelector('[data-kukai-cart-message-count]')
      : null;

    if (!counter) return;

    const max = Number(textarea.getAttribute('maxlength')) || 200;
    counter.textContent = `${textarea.value.length}/${max}`;
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeCart();
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    updateHeaderCartCount();
  });
})();
